import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate, useNavigationType } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { LanguageProvider } from "./contexts/LanguageContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { PaymentOverlayProvider } from "./contexts/PaymentOverlayContext";
import { Toaster } from "./components/ui/sonner";
import { Loader2 } from "lucide-react";

// Pages
import Login from "./pages/Login";
import AuthCallback from "./pages/AuthCallback";
import Dashboard from "./pages/Dashboard";
import CreateEvent from "./pages/CreateEvent";
import EventDetail from "./pages/EventDetail";
import EventWallet from "./pages/EventWallet";
import MediaGallery from "./pages/MediaGallery";
import InvitationPage from "./pages/InvitationPage";
import MediaUploadPage from "./pages/MediaUploadPage";
import AdminPanel from "./pages/AdminPanel";
import Profile from "./pages/Profile";
import Account from "./pages/Account";
import PaymentComplete from "./pages/PaymentComplete";
import TermsOfService from "./pages/TermsOfService";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import RefundPolicy from "./pages/RefundPolicy";
import Home from "./pages/Home";
import About from "./pages/About";
import TemplateGallery from "./pages/TemplateGallery";

// SPAs keep the scroll position across navigations; reset it so every route
// change opens at the top of the page. Browser Back/Forward (POP) is left
// alone so history navigation restores where the user left off.
const ScrollToTop = () => {
  const { pathname } = useLocation();
  const navigationType = useNavigationType();

  useEffect(() => {
    if (navigationType !== "POP") {
      window.scrollTo(0, 0);
    }
  }, [pathname, navigationType]);

  return null;
};

// Landing route. The public Home renders for everyone, signed in or not: the
// wordmark in every app header points here, so bouncing logged-in users to
// /dashboard would make that link impossible to follow. Signing in still lands
// on the dashboard (Login navigates there), and MarketingHeader swaps its CTA
// to "Dashboard" once a session exists.
const HomeRoute = () => <Home />;

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(location.state?.user ? true : null);

  useEffect(() => {
    // If user data passed from AuthCallback, skip auth check
    if (location.state?.user) {
      setIsAuthenticated(true);
      return;
    }

    if (!loading) {
      if (user) {
        setIsAuthenticated(true);
      } else {
        setIsAuthenticated(false);
        navigate('/login', { replace: true });
      }
    }
  }, [user, loading, location.state, navigate]);

  if (loading || isAuthenticated === null) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#ff4d8d]" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return children;
};

const AppRouter = () => {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<HomeRoute />} />
      <Route path="/about" element={<About />} />
      <Route path="/login" element={<Login />} />
      {/* Same page, opened on the signup form — this is where referral links point. */}
      <Route path="/register" element={<Login />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/refund" element={<RefundPolicy />} />
      <Route path="/invite/:inviteLink" element={<InvitationPage />} />
      <Route path="/invite/:inviteLink/share" element={<MediaUploadPage />} />
      {/* The public template gallery, and the preview the create/edit wizard
          iframes per invitation template so the picker shows the real thing. */}
      <Route path="/templates" element={<TemplateGallery />} />
      <Route path="/templates/:templateId" element={<InvitationPage />} />
      <Route path="/payment-complete" element={<PaymentComplete />} />

      {/* Protected Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      } />
      <Route path="/events/create" element={
        <ProtectedRoute>
          <CreateEvent />
        </ProtectedRoute>
      } />
      <Route path="/events/:eventId/edit" element={
        <ProtectedRoute>
          <CreateEvent />
        </ProtectedRoute>
      } />
      <Route path="/events/:eventId/media" element={
        <ProtectedRoute>
          <MediaGallery />
        </ProtectedRoute>
      } />
      <Route path="/events/:eventId/wallet" element={
        <ProtectedRoute>
          <EventWallet />
        </ProtectedRoute>
      } />
      <Route path="/events/:eventId" element={
        <ProtectedRoute>
          <EventDetail />
        </ProtectedRoute>
      } />
      <Route path="/admin" element={
        <ProtectedRoute>
          <AdminPanel />
        </ProtectedRoute>
      } />
      <Route path="/profile" element={
        <ProtectedRoute>
          <Profile />
        </ProtectedRoute>
      } />
      <Route path="/account" element={
        <ProtectedRoute>
          <Account />
        </ProtectedRoute>
      } />
      
      {/* Unknown routes fall back to the public Home page */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter future={{ v7_startTransition: true }}>
        <ScrollToTop />
        <ThemeProvider>
          <LanguageProvider>
            <AuthProvider>
              <PaymentOverlayProvider>
                <AppRouter />
                <Toaster position="top-center" richColors />
              </PaymentOverlayProvider>
            </AuthProvider>
          </LanguageProvider>
        </ThemeProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
